// Unpublished Work © 2022-2024 Deere & Company.

import moment from 'moment';
import {getMyJDTeamMemberByUserId, getMyJDTeamMembers} from 'Services/my-jd-user-service';
import {getTimeOff} from 'Services/membership-service';
import {DATE_FORMATS} from 'Src/ui-core/utils/time-utils';

async function getTimeOffMemberIds(date) {
    const dateFormatted = moment(date).format(DATE_FORMATS.day);
    const {employeeStatus: timeOffs} = await getTimeOff({
        startDate: dateFormatted,
        endDate: dateFormatted
    });

    return new Set(timeOffs.map((m) => m.appUserId));
}

async function filterOutTimeOffMembers(teamMembers, date) {
    try {
        const offMembers = await getTimeOffMemberIds(date);

        if (offMembers.size === 0) {
            return teamMembers;
        }

        return teamMembers.filter((member) => !offMembers.has(member.id));
    } catch {
        return teamMembers;
    }
}

async function tagTimeOffMembers(teamMembers, date) {
    try {
        const offMembers = await getTimeOffMemberIds(date);

        return teamMembers.map((member) => ({
            ...member,
            onTimeOff: offMembers.has(member.id)
        }));
    } catch {
        return teamMembers;
    }
}

async function getTeamMembers(orgId, date, signal, tagOnTimeOff) {
    const teamMembers = await getMyJDTeamMembers(orgId, signal);

    const formattedTeamMembers = teamMembers.map((member) => {
        if (member.authenticatedUser) {
            member.firstName = member.authenticatedUser.firstName;
            member.lastName = member.authenticatedUser.lastName;
        } else if (member.title) {
            member.firstName = member.title;
            member.lastName = '';
        } else {
            member.firstName = member.displayName;
            member.lastName = '';
        }
        return member;
    });

    if (!date) {
        return formattedTeamMembers;
    }

    return tagOnTimeOff ?
        tagTimeOffMembers(formattedTeamMembers, date) :
        filterOutTimeOffMembers(formattedTeamMembers, date);
}

async function fetchAndFilterInactiveOperators(orgId, date, signal, tagOnTimeOff = false) {
    if (!orgId) {
        return [];
    }

    const teamMembers = await getTeamMembers(orgId, date ?? null, signal, tagOnTimeOff);

    return teamMembers;
}

async function fetchAndFormatOperator({
    isMounted,
    setAssignees,
    orgId,
    setTeamMembersMap
}) {
    const teamMembers = await fetchAndFilterInactiveOperators(orgId);
    const teamMembersMap = new Map();

    if (isMounted()) {
        const formattedAssignees = teamMembers.map((teamMember) => {
            teamMembersMap.set(teamMember.id, teamMember);

            return {
                title: `${teamMember.firstName} ${teamMember.lastName}`,
                id: teamMember.id
            };
        }
        );

        setTeamMembersMap(teamMembersMap);
        setAssignees(formattedAssignees);
    }
}

async function fetchUserImageById(orgId, userId) {
    const teamMember = await getMyJDTeamMemberByUserId(orgId, userId);

    if (teamMember && teamMember.onLinkUser?.image) {
        return {
            imageData: teamMember.onLinkUser.image.userImageData,
            extension: teamMember.onLinkUser.image.userImageFileExtension
        };
    }

    return {};
}

export {
    fetchAndFormatOperator,
    fetchAndFilterInactiveOperators,
    fetchUserImageById
};
